import React, { useCallback, useState, useEffect } from 'react';
import { path, propOr, pathOr, isEmpty, includes, equals, sortBy, compose, reverse, prop, isNil } from 'ramda';
import enTranslations from '@shopify/polaris/locales/en.json';
import { useQuery, useMutation } from '@apollo/react-hooks';
import moment from 'moment';
import { GET_JOBS } from '../apollo/queries';
import { UPDATE_JOB_STATUS } from '../apollo/mutation';
import Footer from './Footer';
import {
  Page,
  Layout,
  Button,
  Card,
  Badge,
  Heading,
  DataTable,
  EmptyState,
  AppProvider,
  Frame,
  Filters,
  ChoiceList,
  Subheading,
  Modal,
  TextContainer,
  Spinner,
  Toast,
  ResourceList,
} from '@shopify/polaris';
import { TitleBar } from '@shopify/app-bridge-react';
import { emptyStateImage } from '../../utils/imagesUrl';

const img = 'https://cdn.shopify.com/s/files/1/0757/9955/files/empty-state.svg';

const categories = [
  { label: 'Select Category', value: '' },
  { label: 'Accounting', value: 'accounting' },
  { label: 'Administrative & Office', value: 'administrative and office' },
  { label: 'Cleaning & Facilities', value: 'cleaning and facilities' },
  { label: 'Customer Service', value: 'customer service' },
  { label: 'Design', value: 'design' },
  { label: 'Education', value: 'education' },
  { label: 'Engineering', value: 'engineering' },
  { label: 'Healthcare', value: 'healthcare' },
  { label: 'Human Resources', value: 'human resources' },
  {
    label: 'Installation, Maintenance & Repair',
    value: 'installation, mantenance and repair',
  },
  { label: 'Legal', value: 'legal' },
  { label: 'Management', value: 'managment' },
  { label: 'Marketing', value: 'marketing' },
  { label: 'Merchandising', value: 'merchandising' },
  { label: 'Personal Care Services', value: 'personal care services' },
  { label: 'Hospitality', value: 'hospitality' },
  { label: 'Retail', value: 'retail' },
  { label: 'Sales', value: 'sales' },
  {
    label: 'Social Services & Nonprofit',
    value: 'social services and nonprofit',
  },
  { label: 'Fitness', value: 'fitness' },
  { label: 'Logistics', value: 'logistics' },
  { label: 'Technology', value: 'technology' },
  { label: 'Production', value: 'production' },
  { label: 'Manufacturing', value: 'manufacturing' },
  { label: 'Food Production', value: 'food production' },
  { label: 'eCommerce', value: 'eCommerce' },
  { label: 'Finance', value: 'finance' },
  { label: 'NoCode', value: 'noCode' },
  { label: 'Operations', value: 'operations' },
  { label: 'Public Relations', value: 'public relations' },
];

const JobListTemplate = (props) => {
  let { history, shopifyToken } = props;

  const [updateJobStatus] = useMutation(UPDATE_JOB_STATUS);
  const [sortValue, setSortValue] = useState('DATE_MODIFIED_DESC');
  const [sortedRows, setSortedRows] = useState(null);
  const [isLoading, setLoading] = useState(false);
  const [category, setCategory] = useState(null);
  const [active, setActive] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [queryValue, setQueryValue] = useState(null);
  const [filterCategoryJobs, setFilterCategoryJobs] = useState([]);
  const [filterQueryJobs, setFilterQueryJobs] = useState([]);
  const [category2, setCategory2] = useState(null);
  const [tabJobs, setTabJobs] = useState({});

  const isNilOrEmpty = (value) => isNil(value) || isEmpty(value);

  const handleQueryValueRemove = () => {
    setQueryValue(null);
    if (isNilOrEmpty(category2)) return setFilterQueryJobs(null);

    const valueLowerCase = category2 ? category2.join(' ').toLowerCase() : '';
    setFilterQueryJobs(tabJobs.filter((item) => includes(item.category.toLowerCase(), valueLowerCase)));
  };

  const handleFiltersQueryChange = (value) => {
    setQueryValue(value);

    if (isNilOrEmpty(value) && isNilOrEmpty(category2)) {
      return setFilterQueryJobs(tabJobs);
    }

    if (isNilOrEmpty(value) && !isNilOrEmpty(category2)) {
      return setFilterQueryJobs(filterQueryJobs);
    }

    const valueLowerCase = category2 ? category2.join(' ').toLowerCase() : '';

    setFilterQueryJobs(
      tabJobs.filter(function (item) {
        const searchedTextCondition = item.title.toLowerCase().search(value.toLowerCase()) !== -1;
        const bothConditions = searchedTextCondition && includes(item.category.toLowerCase(), valueLowerCase);

        return isNilOrEmpty(category2) ? searchedTextCondition : bothConditions;
      })
    );
  };

  const filterQuery = (value, array) => {
    if (isEmpty(array)) {
      return setFilterQueryJobs(filterCategoryJobs);
    }

    setFilterQueryJobs(
      array.filter(function (item) {
        return item.title.toLowerCase().search(value.toLowerCase()) !== -1;
      })
    );
  };

  const handleStatusChange = (value) => {
    setCategory2(null);
    setSelectedStatus(value);
    const valueLowerCase = value ? value.join(' ').toLowerCase() : '';
    const queryLowerCase = queryValue ? queryValue.toLowerCase() : '';

    if (isNilOrEmpty(value) && isNilOrEmpty(queryValue)) {
      return setFilterQueryJobs(tabJobs);
    }

    if (!isNilOrEmpty(value) && isNilOrEmpty(queryValue)) {
      return setFilterQueryJobs(
        tabJobs.filter(({ archived }) => {
          if (archived) {
            return includes('archived', valueLowerCase);
          } else {
            return includes('active', valueLowerCase);
          }
        })
      );
    }

    if (isNilOrEmpty(value) && !isNilOrEmpty(queryValue)) {
      return setFilterQueryJobs(tabJobs.filter(({ title }) => includes(queryLowerCase, title.toLowerCase())));
    }

    setFilterQueryJobs(
      tabJobs.filter(({ category, title }) => {
        return includes(queryLowerCase, title.toLowerCase()) && includes(category.toLowerCase(), valueLowerCase);
      })
    );
  };

  const handleCategoryChange = (value) => {
    setCategory2(value);
    setSelectedStatus(null);
    const valueLowerCase = value ? value.join(' ').toLowerCase() : '';
    const queryLowerCase = queryValue ? queryValue.toLowerCase() : '';

    if (isNilOrEmpty(value) && isNilOrEmpty(queryValue)) {
      return setFilterQueryJobs(tabJobs);
    }

    if (!isNilOrEmpty(value) && isNilOrEmpty(queryValue)) {
      return setFilterQueryJobs(tabJobs.filter(({ category }) => includes(category.toLowerCase(), valueLowerCase)));
    }

    if (isNilOrEmpty(value) && !isNilOrEmpty(queryValue)) {
      return setFilterQueryJobs(tabJobs.filter(({ title }) => includes(queryLowerCase, title.toLowerCase())));
    }

    setFilterQueryJobs(
      tabJobs.filter(({ category, title }) => {
        return includes(queryLowerCase, title.toLowerCase()) && includes(category.toLowerCase(), valueLowerCase);
      })
    );
  };

  const handleCategoryRemove = () => {
    setCategory2(null);

    if (queryValue) {
      return setFilterQueryJobs(tabJobs.filter((item) => item.title.toLowerCase().search(queryValue.toLowerCase()) !== -1));
    }
    setFilterQueryJobs(tabJobs);
  };

  const handleStatusRemove = () => {
    setSelectedStatus(null);

    if (queryValue) {
      return setFilterQueryJobs(tabJobs.filter((item) => item.title.toLowerCase().search(queryValue.toLowerCase()) !== -1));
    }
    setFilterQueryJobs(tabJobs);
  };

  const { loading, data, refetch } = useQuery(GET_JOBS, {
    variables: { shopifyToken, templates: true },
  });

  const jobList = propOr([], 'jobs')(data);
  const jobs = propOr([], 'jobs')(jobList);
  const allAccess = propOr(null, 'allAccess')(jobList);
  const changeRoute = path(['history', 'push'])(props);
  // const allJobs   = []

  useEffect(() => {
    refetch();
    setTabJobs(jobs);
    setFilterQueryJobs(jobs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobs]);

  const filters = [
    {
      key: 'category',
      label: 'Category',
      filter: (
        <ChoiceList
          title='Category'
          titleHidden
          choices={categories}
          selected={category2 || []}
          onChange={handleCategoryChange}
          allowMultiple
        />
      ),
      shortcut: true,
    },
    {
      key: 'status',
      label: 'Status',
      filter: (
        <ChoiceList
          title='Status'
          titleHidden
          choices={[
            { label: 'Active', value: 'active' },
            { label: 'Archived', value: 'archived' },
          ]}
          selected={selectedStatus || []}
          onChange={handleStatusChange}
          allowMultiple
        />
      ),
      shortcut: true,
    },
  ];

  const appliedFilters = [];

  if (!isEmpty(category2)) {
    const key = 'category';
    appliedFilters.push({
      key,
      label: disambiguateLabel(key, category2),
      onRemove: handleCategoryRemove,
    });
  }

  if (!isEmpty(selectedStatus)) {
    const key = 'status';
    appliedFilters.push({
      key,
      label: disambiguateLabel(key, selectedStatus),
      onRemove: handleStatusRemove,
    });
  }

  const rows = filterQueryJobs || tabJobs;

  const allJobs = rows.map((item) => [
    <Button className='JobList-Status' onClick={() => changeRoute(`/set-job-template/${item.id}`)} primary plain>
      {' '}
      {item.title}{' '}
    </Button>,
    <p> Jacob Dooley </p>,
    ...Object.values(item).splice(3, 3),
  ]);

  if (jobs.length === 0 && !loading) {
    return (
      <AppProvider>
        <Frame>
          <TitleBar title='Job Templates' breadcrumbs={[{ content: 'Settings', onAction: () => { history.push('/settings'); } }]} />
          <Page title='Job Templates' breadcrumbs={[{ content: 'Settings', onAction: () => { history.push('/settings'); } }]}>
            <Layout>
              <EmptyState
                heading='Manage your job templates'
                action={{ content: 'Create template', onAction: () => changeRoute('/set-job-template') }}
                secondaryAction={{ content: 'Learn more', url: 'https://help.shopify.com' }}
                image={img}>
                <p>Open jobs and start receiving applications from job seekers.</p>
              </EmptyState>
            </Layout>
          </Page>
        </Frame>
      </AppProvider>
    );
  }

  const onRenderListItems = ({ title, category, updatedAt, id, archived }) => {
    const showCategory = categories.filter((c) => c.value == category)[0];
    const onEditTemplate = () => changeRoute(`/set-job-template/${id}`);
    return (
      <ResourceList.Item id={id} accessibilityLabel={`View details for ${title}`}>
        <table>
          <tbody>
            <tr>
              <th style={{ width: '38%' }} onClick={onEditTemplate}>
                <b className='capitalize'>{title}</b>
                <div>
                  <span style={{ fontWeight: '300', fontSize: '12px' }}> Updated {moment(updatedAt).format('MMMM D, YYYY')}</span>
                </div>
              </th>
              <th style={{ width: '38%' }} className='capitalize text-left' onClick={onEditTemplate}>
                {showCategory ? showCategory.label : category}
              </th>
              <th style={{ width: '12%' }} onClick={onEditTemplate}>
                <Badge status={!archived ? 'success' : 'default'}>
                  <span className={'capitalize'}>{archived ? 'Archived' : 'Active'}</span>
                </Badge>
              </th>
              <th style={{ width: '12%' }}>
                <Button onClick={onEditTemplate} fullWidth size='small' ary>
                  Edit
                </Button>
              </th>
            </tr>
          </tbody>
        </table>
      </ResourceList.Item>
    );
  };

  const listSortOptions = [
    { label: 'Recently updated', value: 'RECENTLY_UPDATED' },
    { label: 'Job title A - Z', value: 'TITLE_ASC' },
    { label: 'Job title Z - A', value: 'TITLE_DES' },
  ];

  const onListSortChange = (selected) => {
    setSortValue(selected);

    if (equals(selected, 'RECENTLY_UPDATED')) {
      compose(setFilterQueryJobs, sortBy(prop('updatedAt')))(rows);
    }

    if (equals(selected, 'TITLE_ASC')) {
      compose(setFilterQueryJobs, sortBy(prop('title')))(rows);
    }

    if (equals(selected, 'TITLE_DES')) {
      compose(setFilterQueryJobs, reverse, sortBy(prop('title')))(rows);
    }
  };

  const listResourceName = { singular: 'job', plural: 'jobs' };

  return (
    <AppProvider i18n={enTranslations}>
      <Frame>
        <TitleBar title='Job Templates' breadcrumbs={[{ content: 'Settings', onAction: () => { history.push('/settings'); } }]} />
        <Page
          title='Job Descriptions'
          breadcrumbs={[{ content: 'Settings', onAction: () => { history.push('/settings') } }]}
          primaryAction={{ content: 'Create job description', onAction: () => changeRoute('/set-job-template') }}>
          <Card>
            <Card.Section>
              <Filters
                queryValue={queryValue}
                queryPlaceholder={'Search job descriptions'}
                filters={filters}
                appliedFilters={appliedFilters}
                onQueryChange={handleFiltersQueryChange}
                onQueryClear={handleQueryValueRemove}
                // onClearAll={handleFiltersClearAll}
              />
            </Card.Section>
            <div className='position-relative'>
              <ResourceList
                items={rows}
                renderItem={onRenderListItems}
                resourceName={listResourceName}
                loading={loading}
                sortValue={sortValue}
                sortOptions={listSortOptions}
                onSortChange={onListSortChange}
                emptySearchState={
                  <EmptyState heading='No job description templates found' image={emptyStateImage}>
                    <p>Try changing the filter or search term.</p>
                  </EmptyState>
                }
                emptyState={
                  <EmptyState heading='No job description templates found' image={emptyStateImage}>
                    <p>Create your first job description template.</p>
                  </EmptyState>
                }
              />
            </div>
          </Card>
          <br />
        </Page>
        <Footer link='https://help.shophire.co/article/7-job-description-templates' name='Job description templates' />
      </Frame>
    </AppProvider>
  );

  function disambiguateLabel(key, value) {
    switch (key) {
      case 'category':
        return `Filter by ${value.join(', ')}`;
      case 'productType':
        return value.join(', ');
      case 'status':
        const mappedValue = value
          .map((el) => {
            return `${el.charAt(0).toUpperCase()}${el.slice(1)}`;
          })
          .join(', ');
        return `By status: ${mappedValue}`;
      default:
        return value;
    }
  }

  function isEmpty(value) {
    if (Array.isArray(value)) {
      return value.length === 0;
    } else {
      return value === '' || value == null;
    }
  }
};

export default JobListTemplate;
